import React from 'react'
import { Head } from '@inertiajs/react'
import { Edit3, Trash2, Plus, Check, X } from 'lucide-react'
import { AdminLayout } from '@/components/admin/templates/AdminLayout'
import { Stack } from '@/components/admin/atoms/layout/Stack'
import { Inline } from '@/components/admin/atoms/layout/Inline'
import { Grid } from '@/components/admin/atoms/layout/Grid'
import { ScrollArea } from '@/components/admin/atoms/layout/ScrollArea'
import { Heading } from '@/components/admin/atoms/typography/Heading'
import { Text } from '@/components/admin/atoms/typography/Text'
import { Muted } from '@/components/admin/atoms/typography/Muted'
import { Badge } from '@/components/admin/atoms/feedback/Badge'
import { EmptyState } from '@/components/admin/atoms/feedback/EmptyState'
import { ActionButton } from '@/components/admin/atoms/actions/ActionButton'
import { IconButton } from '@/components/admin/atoms/actions/IconButton'
import { TextInput } from '@/components/admin/atoms/inputs/TextInput'
import { FormField } from '@/components/admin/molecules/form/FormField'
import { FormSection } from '@/components/admin/molecules/form/FormSection'
import { ConfirmDialog } from '@/components/admin/organisms/ConfirmDialog'
import { Divider } from '@/components/admin/atoms/layout/Divider'
import { DataTable } from '@/components/admin/atoms/data/DataTable'
import { DataTableHead } from '@/components/admin/atoms/data/DataTableHead'
import { DataTableHeaderCell } from '@/components/admin/atoms/data/DataTableHeaderCell'
import { DataTableBody } from '@/components/admin/atoms/data/DataTableBody'
import { DataTableRow } from '@/components/admin/atoms/data/DataTableRow'
import { DataTableCell } from '@/components/admin/atoms/data/DataTableCell'
import { useTagsIndexVM } from './Index.vm'
import type { PageProps } from '@/types/inertia.d'
import type { Tag } from '@/types/admin'

type Props = PageProps<{ tags: Tag[] }>

const TagsIndex: React.FC<Props> = ({ tags: initialTags }) => {
  const vm = useTagsIndexVM(initialTags)

  return (
    <AdminLayout breadcrumbs={[{ label: 'Tags' }]}>
      <Head title="Tags" />
      <Stack gap="6">
        <Heading level={2}>Tags</Heading>

        <Grid cols="2" gap="6">
          <FormSection>
            <Inline justify="between" align="center">
              <Text size="sm" weight="semibold">All Tags</Text>
              <Muted>{vm.tags.length} total</Muted>
            </Inline>
            <Divider />

            {vm.tags.length === 0 ? (
              <EmptyState title="No tags" description="Create your first tag." />
            ) : (
              <ScrollArea horizontal>
                <DataTable>
                  <DataTableHead>
                    <DataTableHeaderCell>Name</DataTableHeaderCell>
                    <DataTableHeaderCell>Slug</DataTableHeaderCell>
                    <DataTableHeaderCell align="center">Posts</DataTableHeaderCell>
                    <DataTableHeaderCell width="80px" />
                  </DataTableHead>
                  <DataTableBody>
                    {vm.tags.map((tag) => (
                      <DataTableRow key={tag.id}>
                        {vm.isEditing(tag.id) ? (
                          <>
                            <DataTableCell>
                              <TextInput value={vm.editName} onChange={vm.setEditName} />
                            </DataTableCell>
                            <DataTableCell>
                              <TextInput value={vm.editSlug} onChange={vm.setEditSlug} />
                            </DataTableCell>
                            <DataTableCell align="center">
                              <Muted>{tag.posts_count ?? 0}</Muted>
                            </DataTableCell>
                            <DataTableCell>
                              <Inline gap="1">
                                <IconButton icon={<Check style={{ width: 16, height: 16 }} />} label="Save" variant="outline" onClick={vm.handleUpdate} disabled={vm.saving} />
                                <IconButton icon={<X style={{ width: 16, height: 16 }} />} label="Cancel" onClick={vm.cancelEdit} />
                              </Inline>
                            </DataTableCell>
                          </>
                        ) : (
                          <>
                            <DataTableCell>
                              <Text size="sm" weight="medium">{tag.name}</Text>
                            </DataTableCell>
                            <DataTableCell>
                              <Muted>{tag.slug}</Muted>
                            </DataTableCell>
                            <DataTableCell align="center">
                              <Badge variant="ghost">{tag.posts_count ?? 0}</Badge>
                            </DataTableCell>
                            <DataTableCell>
                              <Inline gap="1">
                                <IconButton icon={<Edit3 style={{ width: 16, height: 16 }} />} label="Edit" onClick={() => vm.startEdit(tag)} />
                                <IconButton icon={<Trash2 style={{ width: 16, height: 16 }} />} label="Delete" variant="error" onClick={() => vm.setDeleteId(tag.id)} />
                              </Inline>
                            </DataTableCell>
                          </>
                        )}
                      </DataTableRow>
                    ))}
                  </DataTableBody>
                </DataTable>
              </ScrollArea>
            )}
          </FormSection>

          <FormSection>
            <Text size="sm" weight="semibold">Add Tag</Text>
            <Divider />
            <FormField label="Name" htmlFor="tag_name" required error={vm.createError || undefined}>
              <TextInput id="tag_name" value={vm.newName} onChange={vm.handleNewNameChange} placeholder="Tag name" />
            </FormField>
            <FormField label="Slug" htmlFor="tag_slug">
              <TextInput id="tag_slug" value={vm.newSlug} onChange={vm.setNewSlug} placeholder="tag-slug" />
            </FormField>
            <ActionButton variant="primary" onClick={vm.handleCreate} loading={vm.creating} disabled={vm.createDisabled}>
              <Plus style={{ width: 16, height: 16 }} />
              Save Tag
            </ActionButton>
          </FormSection>
        </Grid>

        {vm.tags.length > 0 && (
          <FormSection>
            <Text size="sm" weight="semibold">Tag Cloud</Text>
            <Divider />
            <Inline gap="2" wrap>
              {vm.tags.map((tag) => (
                <Badge key={tag.id} variant="ghost">
                  {tag.name}
                  {tag.posts_count ? <Muted> ({tag.posts_count})</Muted> : null}
                </Badge>
              ))}
            </Inline>
          </FormSection>
        )}
      </Stack>

      <ConfirmDialog
        open={vm.deleteDialogOpen}
        title="Delete Tag"
        description="This will remove the tag from all posts. This action cannot be undone."
        onConfirm={vm.handleDelete}
        onCancel={() => vm.setDeleteId(null)}
        loading={vm.deleting}
      />
    </AdminLayout>
  )
}

export default TagsIndex
